<template>
  <div class="v1-code-card" :data-testid="dataTestid">
    <div class="v1-code-card__icon">
      <img
        v-if="code.icon.kind === 'logo'"
        :src="code.icon.url"
        :alt="code.title || code.label"
        class="v1-code-card__logo"
        :style="logoMaxHeight ? { maxHeight: logoMaxHeight } : undefined"
        data-testid="v1-code-card-logo"
      />
      <span v-else class="v1-code-card__glyph" data-testid="v1-code-card-glyph">
        <svg v-if="code.icon.name === 'bank'" viewBox="0 0 21 21" width="20" height="20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M18.6473 2.35266C18.4823 2.18772 18.2763 2.06976 18.0505 2.01093C17.8247 1.9521 17.5873 1.95453 17.3627 2.01797H17.3504L1.60535 6.79547C1.34974 6.86914 1.12254 7.01874 0.953875 7.22444C0.785206 7.43015 0.683026 7.68226 0.660876 7.94735C0.638726 8.21244 0.69765 8.47801 0.829842 8.70886C0.962033 8.9397 1.16125 9.12493 1.40109 9.24L8.36719 12.6328L11.7551 19.5948C11.8605 19.8197 12.028 20.0097 12.2379 20.1424C12.4478 20.2751 12.6913 20.345 12.9396 20.3437C12.9773 20.3437 13.0151 20.3421 13.0528 20.3388C13.3177 20.3174 13.5697 20.2154 13.775 20.0467C13.9802 19.8779 14.129 19.6504 14.2013 19.3946L18.9755 3.64957C18.9755 3.64547 18.9755 3.64137 18.9755 3.63727C19.0397 3.41329 19.0432 3.17625 18.9855 2.95048C18.9279 2.72472 18.8111 2.51838 18.6473 2.35266ZM12.947 19.0189L12.9429 19.0304V19.0247L9.65672 12.2735L13.5942 8.33602C13.7121 8.21194 13.7768 8.04673 13.7746 7.87561C13.7725 7.70448 13.7035 7.54098 13.5825 7.41997C13.4615 7.29896 13.298 7.23 13.1269 7.22781C12.9557 7.22562 12.7905 7.29037 12.6664 7.40824L8.72895 11.3457L1.97531 8.05957H1.96957H1.98105L17.7188 3.28125L12.947 19.0189Z"
          />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M3 5h2v14H3V5Zm3 0h1v14H6V5Zm2 0h2v14H8V5Zm3 0h1v14h-1V5Zm2 0h2v14h-2V5Zm3 0h1v14h-1V5Zm2 0h2v14h-2V5Z"
          />
        </svg>
      </span>
    </div>

    <p class="v1-code-card__title" data-testid="v1-code-card-title">{{ code.title || code.label }}</p>

    <barCode
      v-if="isBarcode"
      :value="code.value"
      format="CODE128"
      class="v1-code-card__barcode"
      :display-value="false"
      background="transparent"
      height="48"
      margin-top="1"
      margin-bottom="1"
      data-testid="v1-code-card-barcode"
    >
      {{ t("barcode-not-created") }}
    </barCode>

    <div v-if="isQr" ref="qrContainer" class="v1-code-card__qr" data-testid="v1-code-card-qr">
      <qrcode-vue :value="code.value" :size="180" level="H" background="transparent" render-as="svg" />
    </div>

    <p
      v-if="showValue"
      class="v1-code-card__value"
      :style="valueFontSize ? { fontSize: valueFontSize } : undefined"
      data-testid="v1-code-card-value"
    >
      {{ displayValue }}
    </p>

    <p
      v-if="code.caption"
      class="v1-code-card__caption"
      :style="captionFontSize ? { fontSize: captionFontSize } : undefined"
      data-testid="v1-code-card-caption"
    >
      {{ code.caption }}
    </p>

    <div v-if="hasActions" class="v1-code-card__actions">
      <Button
        v-if="showCopy"
        variant="neutral-tonal"
        size="lg"
        class="v1-code-card__pill"
        data-testid="v1-code-card-copy"
        @click="copy"
      >
        {{ copyLabel || (qrImageCopy && isQr ? t("copy-qr") : t("copy-code")) }}
        <template #icon-trailing>
          <img src="@/assets/icons/copy.svg" alt="" aria-hidden="true" class="v1-code-card__pill-icon" />
        </template>
      </Button>
      <Button
        v-if="showShare && !qrDownload"
        variant="neutral-tonal"
        size="lg"
        class="v1-code-card__pill"
        data-testid="v1-code-card-share"
        @click="share"
      >
        {{ t("share") }}
        <template #icon-trailing>
          <img src="@/assets/icons/share.svg" alt="" aria-hidden="true" class="v1-code-card__pill-icon" />
        </template>
      </Button>
      <Button
        v-if="qrDownload && isQr"
        variant="neutral-tonal"
        size="lg"
        class="v1-code-card__pill"
        data-testid="v1-code-card-download"
        @click="download"
      >
        {{ t("download") }}
        <template #icon-trailing>
          <img src="@/assets/icons/download.svg" alt="" aria-hidden="true" class="v1-code-card__pill-icon" />
        </template>
      </Button>
      <Button
        v-if="invoice"
        variant="neutral-tonal"
        size="lg"
        class="v1-code-card__pill"
        data-testid="v1-code-card-invoice"
        @click="openInvoice"
      >
        {{ invoice.label }}
        <template #icon-trailing>
          <img src="@/assets/icons/eye.svg" alt="" aria-hidden="true" class="v1-code-card__pill-icon" />
        </template>
      </Button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import Vue3Barcode from "vue3-barcode";
import QrcodeVue from "qrcode.vue";
import { showToast } from "@/helpers/showToast";
import { formatReferenceDisplay } from "@/helpers/provider.helpers";
import { copyQrSvgAsPng, downloadQrSvg } from "@/helpers/qrImage.helper";
import { IV1ReferenceCode, V1CodeFormat } from "@/types/IV1PaymentReference";
import { Button } from "@payjoy/design-system";

/**
 * Hero code card (redesign §3a) — the focal element of the payment-reference
 * screen. Renders a code's leading icon (brand logo or blue type-glyph), title,
 * the scannable graphic (barcode/QR drawn locally from `code.value`), the code
 * value, its server-composed caption, and the Copy / Share pills (DS `Button`
 * `neutral-tonal`).
 *
 * The card itself has no design-system equivalent, so it is built by hand. Share
 * uses the Web Share API when available (native share sheet inside the webview)
 * and falls back to copying the value to the clipboard otherwise.
 */
export default defineComponent({
  name: "V1ReferenceCodeCard",
  components: {
    barCode: Vue3Barcode,
    QrcodeVue,
    Button,
  },
  props: {
    code: {
      type: Object as PropType<IV1ReferenceCode>,
      required: true,
    },
    dataTestid: {
      type: String,
      required: false,
      default: "v1-code-card",
    },
    copyValue: { type: String, required: false, default: "" },
    copyLabel: { type: String, required: false, default: "" },
    showValue: { type: Boolean, required: false, default: true },
    showCopy: { type: Boolean, required: false, default: true },
    showShare: { type: Boolean, required: false, default: true },
    qrImageCopy: { type: Boolean, required: false, default: false },
    qrDownload: { type: Boolean, required: false, default: false },
    invoice: {
      type: Object as PropType<{ label: string; url: string } | null>,
      required: false,
      default: null,
    },
    valueFontSize: { type: String, required: false, default: "" },
    captionFontSize: { type: String, required: false, default: "" },
    logoMaxHeight: { type: String, required: false, default: "" },
  },
  emits: ["copy"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const isBarcode = computed<boolean>(() => props.code.format === V1CodeFormat.Barcode);
    const isQr = computed<boolean>(() => props.code.format === V1CodeFormat.Qr);

    const qrContainer = ref<HTMLElement | null>(null);

    const displayValue = computed<string>(() => formatReferenceDisplay(props.code.display ?? props.code.value));
    const copyText = computed<string>(() => props.copyValue || props.code.value);
    const hasActions = computed<boolean>(
      () => props.showCopy || (props.showShare && !props.qrDownload) || (props.qrDownload && isQr.value) || !!props.invoice,
    );
    const qrSvg = (): SVGSVGElement | null => qrContainer.value?.querySelector("svg") ?? null;

    // Constrained webviews can reject clipboard writes (no permission / no
    // secure context); surface a toast instead of letting the promise reject
    // unhandled and leaving the user with no feedback.
    const copyToClipboard = async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        showToast(t("copied"));
        return true;
      } catch {
        showToast(t("copy-failed"));
        return false;
      }
    };

    const copyQrImage = async (): Promise<boolean> => {
      try {
        const copied = await copyQrSvgAsPng(qrSvg(), copyText.value);
        if (copied) showToast(t("copied"));
        return copied;
      } catch {
        showToast(t("copy-failed"));
        return false;
      }
    };

    const copy = async (): Promise<void> => {
      const copied = props.qrImageCopy && isQr.value ? await copyQrImage() : await copyToClipboard(copyText.value);
      if (copied) emit("copy");
    };

    const download = (): void => {
      downloadQrSvg(qrSvg());
    };

    const openInvoice = (): void => {
      if (props.invoice) window.open(props.invoice.url, "_self");
    };

    const share = async (): Promise<void> => {
      const text = copyText.value;
      if (typeof navigator.share === "function") {
        try {
          await navigator.share({ text });
          return;
        } catch (shareError) {
          // A user-cancelled share sheet is not an error; only fall back to the
          // clipboard when the API is unavailable/failed, not on cancellation.
          if ((shareError as DOMException)?.name === "AbortError") return;
        }
      }
      await copyToClipboard(text);
    };

    return { t, qrContainer, isBarcode, isQr, displayValue, hasActions, copy, share, download, openInvoice };
  },
});
</script>

<style scoped>
.v1-code-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: var(--pj-space-6, 24px) var(--pj-space-2, 8px) var(--pj-space-3, 12px);
  border: 1px solid var(--pj-color-brand-gray-350, #c6c7bf);
  border-radius: var(--pj-radius-xl, 16px);
  background: var(--pj-color-brand-white, #ffffff);
  text-align: center;
}

@media (min-width: 768px) {
  .v1-code-card {
    padding: var(--pj-space-6, 24px) var(--pj-space-4, 16px) var(--pj-space-4, 16px);
  }
}

.v1-code-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.v1-code-card__logo {
  max-height: 40px;
  max-width: 120px;
  object-fit: contain;
}

.v1-code-card__glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--pj-radius-sm, 4px);
  background: var(--pj-color-brand-blue-050, #ecf0ff);
  color: var(--pj-color-brand-gray-850, #222926);
}

.v1-code-card__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-650, #3f4340);
}

.v1-code-card__barcode {
  /* Match the legacy screen: render at natural width (no fixed cap) so long
     CODE128 references (e.g. PAYNET/Walmart) keep bars wide enough to scan. */
  width: 100%;
  max-width: 100%;
}

.v1-code-card__qr {
  display: flex;
  justify-content: center;
  width: 100%;
}

.v1-code-card__value {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 29px;
  color: var(--pj-color-brand-gray-850, #222926);
  word-break: break-word;
  overflow-wrap: anywhere;
}

.v1-code-card__caption {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.v1-code-card__actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
}

.v1-code-card__pill {
  flex: 1;
}

.v1-code-card__pill-icon {
  width: 20px;
  height: 20px;
}
</style>
